import { useCallback, useEffect, useRef, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import {
  CheckoutSalesLocationTable,
  BoxPickUpStation,
  OrderDelivery,
  OrderFormat,
  SalesLocationType,
  ServiceTime,
  ServiceTimeKindType,
} from '@ancon/wildcat-types'
import { getOutletOrderFormats } from '@ancon/wildcat-utils/outlet'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import isAOTippingEnabledForOrderFormat from '@ancon/wildcat-utils/outlet/isAOTippingEnabledForOrderFormat'

import Modal from '../../../app/components/modal/Modal'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import {
  outletListItemSelector,
  outletFiltersSelector,
  outletOrderConfigFilterModalSelector,
} from '../../store/outletSelector'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import {
  outletForceRefreshMenuIfNeeded,
  outletSetIsVisibleBoxPickupOrderConfigureModal,
  outletSetIsVisibleDeliveryLocationModal,
  outletSetIsVisibleTableOrderConfigureModal,
  outletSetOrderConfigureFilterModal,
  outletSetOutletFilters,
} from '../../store/outletSlice'
import { ModalHeaderProps } from '../../../app/components/modal/ModalHeader'
import useAppStore from '../../../../store/hooks/useAppStore'
import useRouteMatch from '../../../app/hooks/useRouteMatch'
import { AppRoutes, OrderFormatSlugMap } from '../../../app/constants'
import {
  checkoutCurrentCheckoutDetailsDeliveryAddressSelector,
  checkoutCurrentCheckoutOutletListItemSelector,
  checkoutFiltersOrderFormatSelector,
  checkoutFiltersSelector,
  checkoutHasAdyenPaymentMethodsSelector,
  checkoutIsSkipTipModalSelector,
  checkoutIsSystemProductSelector,
  checkoutIsTippingAppliedSelector,
  outletSelectedOutletHasCheckoutSelector,
} from '../../../checkout/store/checkoutSelectors'
import DeliveryLocationConfigureModal from '../../../outlets/components/deliveryLocationConfigureModal/DeliveryLocationConfigureModal'
import {
  checkoutSetCheckoutFilters,
  checkoutUpdatePenguinLockersMetaData,
  checkoutUpdateTipState,
} from '../../../checkout/store/checkoutSlice'
import { updateCheckout } from '../../../checkout/store/checkoutThunks'
import TableOrderConfigureModal from '../TableOrderConfigureModal'
import { OrderConfigureFilterMode } from '../../types'
import BoxPickupOrderConfigureModal from '../boxPickupOrderConfigureModal/BoxPickupOrderConfigureModal'
import { AddTipModalInitialState } from '../../../app/components/addTipModal/constants'
import { TipModalType } from '../../../app/components/addTipModal/types'

import styles from './OrderConfigurationModal.module.scss'
import OrderFormatSelectionStep from './OrderFormatSelectionStep'
import OrderTimeSelectionStep from './OrderTimeSelectionStep'
import OrderConfigurationModalHeader from './OrderConfigurationModalHeader'

export default function OrderConfigurationModal() {
  const { t } = useTranslation('common')

  const dispatch = useAppDispatch()
  const store = useAppStore()
  const router = useRouter()

  const [isFirstStep, setIsFirstStep] = useState(true)
  const [selectedOrderFormat, setSelectedOrderFormat] = useState<OrderFormat>()
  const [serviceTime, setServiceTime] = useState<ServiceTime>({
    kind: ServiceTimeKindType.AsSoonAsPossible,
    time: '',
  })
  const [deliveryLocation, setDeliveryLocation] = useState<OrderDelivery>()
  const [salesLocation, setSalesLocation] =
    useState<CheckoutSalesLocationTable>()
  const [selectedSection, setSelectedSection] = useState<BoxPickUpStation>()

  // The visible URL can differ from the page after a back navigation to a
  // history entry with a masked URL, so the page's own route counts too
  const isCheckoutPage =
    useRouteMatch([AppRoutes.Checkout]) ||
    router.pathname === AppRoutes.Checkout

  const isOrderFormatChangeRef = useRef<boolean>(false)

  const modalMode = useAppSelector(outletOrderConfigFilterModalSelector)
  const outletListItem = useAppSelector(outletListItemSelector)!
  const checkoutOutletListItem = useAppSelector(
    checkoutCurrentCheckoutOutletListItemSelector,
  )
  const checkoutDeliveryAddress = useAppSelector(
    checkoutCurrentCheckoutDetailsDeliveryAddressSelector,
  )

  const outlet = isCheckoutPage ? checkoutOutletListItem : outletListItem

  const orderFormats = outlet ? getOutletOrderFormats(outlet) : []

  const initialDeliveryAddress = isCheckoutPage
    ? checkoutDeliveryAddress
    : deliveryLocation?.address

  const isOrderTypeFilterModal =
    modalMode === OrderConfigureFilterMode.OrderType
  const isOrderTimeFilterModal =
    modalMode === OrderConfigureFilterMode.OrderTime

  function handleOnChangeOrderFormat(orderFormat: OrderFormat) {
    const isDelivery = orderFormat === OrderFormat.Delivery
    const isTableOrder = orderFormat === OrderFormat.TableOrder
    const isBoxPickup = orderFormat === OrderFormat.BoxPickup

    isOrderFormatChangeRef.current = true

    if (isDelivery) {
      dispatch(outletSetIsVisibleDeliveryLocationModal(true))
      return
    }

    if (isTableOrder) {
      dispatch(outletSetIsVisibleTableOrderConfigureModal(true))
      setServiceTime({
        kind: ServiceTimeKindType.AsSoonAsPossible,
        time: '',
      })
      return
    }

    if (isBoxPickup) {
      dispatch(outletSetIsVisibleBoxPickupOrderConfigureModal(true))
      return
    }

    setSelectedOrderFormat(orderFormat)
  }

  function handleOnClose() {
    dispatch(outletSetOrderConfigureFilterModal(OrderConfigureFilterMode.None))
    isOrderFormatChangeRef.current = false
  }

  function handleOnScheduleOrder() {
    setIsFirstStep(false)
  }

  useEffect(() => {
    // Reset state when modal is closed
    if (!modalMode) {
      setIsFirstStep(true)
    } else {
      const getFilters = isCheckoutPage
        ? checkoutFiltersSelector
        : outletFiltersSelector
      const outletFilters = getFilters(store.getState())

      if (isOrderTimeFilterModal) {
        setIsFirstStep(false)
      }

      if (outletFilters) {
        setSelectedOrderFormat(outletFilters.orderFormat!)

        if (outletFilters.serviceTime) {
          setServiceTime(outletFilters.serviceTime)
        }
      }
    }
  }, [store, isCheckoutPage, modalMode, isOrderTimeFilterModal])

  function handleOnPressBack() {
    setIsFirstStep(true)
  }

  const CustomHeader = useCallback(
    (headerProps: ModalHeaderProps) => (
      <OrderConfigurationModalHeader
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...headerProps}
        onBack={handleOnPressBack}
        isSecondStep={
          modalMode === OrderConfigureFilterMode.OrderTypeAndTime &&
          !isFirstStep
        }
      />
    ),
    [isFirstStep, modalMode],
  )

  function updateCheckoutTipConfigurations() {
    // Update AO Tipping
    const isAOTippingEnabled = isAOTippingEnabledForOrderFormat(
      outlet!,
      selectedOrderFormat!,
    )

    if (isAOTippingEnabled && isCheckoutPage) {
      const hasAdyenPaymentMethods = checkoutHasAdyenPaymentMethodsSelector(
        store.getState(),
      )
      const isSkipTipModal = checkoutIsSkipTipModalSelector(store.getState())

      if (hasAdyenPaymentMethods && isSkipTipModal !== true) {
        const { isSkipTipModal: isSkipTipModalInitial, ...rest } =
          AddTipModalInitialState

        dispatch(
          checkoutUpdateTipState({
            ...rest,
            visibleModalType: TipModalType.Info,
          }),
        )
      }
    } else {
      const isTipAdded = checkoutIsTippingAppliedSelector(store.getState())

      if (isTipAdded) {
        dispatch(checkoutUpdateTipState(AddTipModalInitialState))
      }
    }
  }

  async function handleOnApplyLocation(orderDelivery?: OrderDelivery) {
    const outletSelectedOutletHasCheckout =
      outletSelectedOutletHasCheckoutSelector(store.getState())

    if (isOrderFormatChangeRef?.current) {
      setDeliveryLocation(orderDelivery)
      setSelectedOrderFormat(OrderFormat.Delivery)
    } else if (isCheckoutPage || outletSelectedOutletHasCheckout) {
      dispatch(
        checkoutSetCheckoutFilters({
          orderFormat: OrderFormat.Delivery,
          delivery: orderDelivery,
        }),
      )

      const orderInstructions = orderDelivery?.address?.note?.trim()

      try {
        await dispatch(
          updateCheckout({
            instructions: orderInstructions,
          }),
        ).unwrap()
        updateCheckoutTipConfigurations()
      } catch (error) {
        // Handled error
      }
    }

    if (!isOrderFormatChangeRef?.current && !isCheckoutPage) {
      dispatch(
        outletSetOutletFilters({
          orderFormat: OrderFormat.Delivery,
          delivery: orderDelivery,
        }),
      )
    }
  }

  async function handleOnUpdate(newServiceTime?: ServiceTime) {
    const outletSelectedOutletHasCheckout =
      outletSelectedOutletHasCheckoutSelector(store.getState())
    const isSystemProductCheckout = checkoutIsSystemProductSelector(
      store.getState(),
    )
    const nextSalesLocation =
      selectedOrderFormat === OrderFormat.TableOrder
        ? {
            type: SalesLocationType.Table,
            table: salesLocation,
          }
        : null

    if (!isCheckoutPage) {
      const prevOrderFormat = outletFiltersSelector(
        store.getState(),
      )?.orderFormat
      const isOrderFormatChanged = prevOrderFormat !== selectedOrderFormat

      if (isOrderFormatChanged) {
        router.query.orderFormat =
          OrderFormatSlugMap[selectedOrderFormat as never]
        router.push(router, undefined, { shallow: true })
      }

      const outletFilters = isOrderTypeFilterModal
        ? {
            orderFormat: selectedOrderFormat!,
            delivery: deliveryLocation,
            salesLocation: nextSalesLocation,
            ...(selectedSection && { section: selectedSection, serviceTime }),
          }
        : {
            orderFormat: selectedOrderFormat!,
            serviceTime: newServiceTime || serviceTime || null,
            delivery: deliveryLocation,
            salesLocation: nextSalesLocation,
            ...(selectedSection && { section: selectedSection }),
          }

      dispatch(outletSetOutletFilters(outletFilters))
    }

    if (
      isCheckoutPage ||
      (outletSelectedOutletHasCheckout && !isSystemProductCheckout)
    ) {
      const checkoutOrderFormat = checkoutFiltersOrderFormatSelector(
        store.getState(),
      )
      const isOrderFormatChanged = checkoutOrderFormat !== selectedOrderFormat

      dispatch(
        checkoutSetCheckoutFilters({
          orderFormat: selectedOrderFormat!,
          serviceTime: newServiceTime || serviceTime || null,
          delivery: deliveryLocation,
          salesLocation: nextSalesLocation,
          ...(selectedSection && { section: selectedSection }),
        }),
      )

      const orderInstructions = deliveryLocation?.address?.note?.trim()

      try {
        await dispatch(
          updateCheckout({
            instructions: orderInstructions,
          }),
        ).unwrap()

        if (isCheckoutPage && isOrderFormatChanged) {
          updateCheckoutTipConfigurations()
        }
      } catch (error) {
        // Handled error
      }
    }

    dispatch(outletSetOrderConfigureFilterModal(OrderConfigureFilterMode.None))
  }

  async function handleOnSuccessTableOrder(table: CheckoutSalesLocationTable) {
    if (isOrderFormatChangeRef?.current) {
      setSalesLocation(table)
      setSelectedOrderFormat(OrderFormat.TableOrder)
    } else {
      dispatch(
        checkoutSetCheckoutFilters({
          orderFormat: OrderFormat.TableOrder,
          salesLocation: {
            type: SalesLocationType.Table,
            table,
          },
        }),
      )

      try {
        await dispatch(updateCheckout({})).unwrap()
        updateCheckoutTipConfigurations()
      } catch (error) {
        // Handled error
      }
    }
  }

  function handleOnChangeOrderTime(newServiceTime: ServiceTime) {
    if (isOrderTimeFilterModal) {
      const isBoxPickup = selectedOrderFormat === OrderFormat.BoxPickup

      if (isBoxPickup) {
        dispatch(
          checkoutUpdatePenguinLockersMetaData({
            timeBegin: null,
            expireTime: null,
            pickingTime: null,
            preferredServiceTime: newServiceTime.time,
          }),
        )
      }

      handleOnUpdate(newServiceTime)

      const isOrderNow =
        newServiceTime.kind === ServiceTimeKindType.AsSoonAsPossible

      if (isOrderNow && serviceTime.kind === newServiceTime.kind) {
        dispatch(outletForceRefreshMenuIfNeeded())
      }
    } else {
      setServiceTime(newServiceTime)
      setIsFirstStep(true)
    }
  }

  function getModalTitle() {
    if (isOrderTypeFilterModal) {
      return t('orderConfigurationModal.howDoYouWantToOrder')
    }

    return t(
      `orderConfigurationModal.${isFirstStep ? 'howAndWhen' : 'scheduleOrder'}`,
    )
  }

  function handleOnUpdateBoxPickupModal({
    serviceTime: newServiceTime,
    section,
  }: {
    section: BoxPickUpStation
    serviceTime: ServiceTime
  }) {
    setSelectedOrderFormat(OrderFormat.BoxPickup)
    setServiceTime(newServiceTime)
    setSelectedSection(section)
  }

  return (
    <>
      <Modal
        title={getModalTitle()}
        isOpen={!!modalMode}
        onClose={handleOnClose}
        mobileModalMode={isFirstStep ? 'bottom-sheet' : 'full-screen'}
        className={clsx(styles.modal, {
          [styles.minContent]: !isFirstStep,
          [styles.fitContent]:
            modalMode === OrderConfigureFilterMode.OrderTypeAndTime &&
            isFirstStep,
        })}
        CustomModalHeader={CustomHeader}
      >
        {isFirstStep && (
          <OrderFormatSelectionStep
            orderFormats={orderFormats}
            selectedOrderFormat={selectedOrderFormat}
            selectedServiceTime={serviceTime}
            onChangeOrderFormat={handleOnChangeOrderFormat}
            onScheduleOrder={handleOnScheduleOrder}
            onUpdate={handleOnUpdate}
          />
        )}
        {!isFirstStep && (
          <OrderTimeSelectionStep
            selectedOrderFormat={selectedOrderFormat!}
            initialServiceTime={serviceTime}
            outletListItem={outlet!}
            onApply={handleOnChangeOrderTime}
          />
        )}
      </Modal>
      <DeliveryLocationConfigureModal
        initialAddress={initialDeliveryAddress}
        isCheckoutPage={isCheckoutPage}
        onClickContinue={handleOnApplyLocation}
      />
      <TableOrderConfigureModal
        isCheckoutPage={isCheckoutPage}
        onSuccess={handleOnSuccessTableOrder}
      />
      <BoxPickupOrderConfigureModal
        onClickContinue={handleOnUpdateBoxPickupModal}
      />
    </>
  )
}
